import { Component, EventEmitter, OnInit, Output, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Util } from "@common/utils/util";
import { ACCOUNTS } from "@constants/data-service.constant";
import { ModalService } from "@services/modal/modal.service";
import { StepsService } from "@services/steps/steps.service";
import { StorageService } from "@services/storage/storage.service";
import { GenericErrorOps } from "@shared/components/generic-error/interfaces/generic-error-ops.interface";
import { OutlineInputComponent } from "@shared/components/outline-input/outline-input.component";

@Component({
    selector: "app-congelamiento-saldos-cuentas-voluntarias",
    templateUrl: "./congelamiento-saldos-cuentas-voluntarias.component.html",
    styleUrls: ["./congelamiento-saldos-cuentas-voluntarias.component.scss"],
    standalone: false
})
export class CongelamientoSaldosCuentasVoluntariasComponent implements OnInit {
  public loading: boolean = false;
  public error: boolean = false;
  public enableButtonRetry: boolean = false;
  public errorOps: GenericErrorOps;
  public ccvAmount: number = 0;
  public cavAmount: number = 0;
  public ccvSelectedAmount: number = 0;
  public cavSelectedAmount: number = 0;
  public selectedAmount: number = 0;
  public ccvCheckbox: boolean = false;
  public cavCheckbox: boolean = false;
  public ccvSelectedPercentage: number = 0;
  public cavSelectedPercentage: number = 0;
  public ccvFormGroup = new FormGroup({
    ccvPercentage: new FormControl("", [Validators.required]),
  });
  public cavFormGroup = new FormGroup({
    cavPercentage: new FormControl("", [Validators.required]),
  });
  @Output() ccvSelectedAmountChangeEvent = new EventEmitter<number>();  
  @Output() cavSelectedAmountChangeEvent = new EventEmitter<number>();
  @Output() ccvCheckboxChange = new EventEmitter<boolean>();
  @Output() cavCheckboxChange = new EventEmitter<boolean>();
  @Output() isCCVCorrectlySelectedEvent = new EventEmitter<boolean>();
  @Output() isCAVCorrectlySelectedEvent = new EventEmitter<boolean>();
  @ViewChild("ccvOutlineInput") ccvOutlineInput: OutlineInputComponent;
  @ViewChild("cavOutlineInput") cavOutlineInput: OutlineInputComponent;
  @Output() ccvOutPercentage = new EventEmitter<number>();
  @Output() cavOutPercentage = new EventEmitter<number>();

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly stepsService: StepsService,
    private readonly storageService: StorageService,
    private readonly modalService: ModalService,
    private readonly utils: Util
  ) {
    stepsService.setCurrentStep(1);
  }

  ngOnInit() {
    void this.initializeComponent();
  }
  
  private async initializeComponent() {
    const clientInfo = await this.storageService.get(ACCOUNTS);
    const accounts = clientInfo;
    this.setAccountAmounts(accounts);

    this.ccvFormGroup.get("ccvPercentage")?.setValue("");
    this.cavFormGroup.get("cavPercentage")?.setValue("");
  }

  checkCCVCorrectlySelected() {
    const isCCVCorrectlySelected = this.isCCVCorrectlySelected();
    this.isCCVCorrectlySelectedEvent.emit(isCCVCorrectlySelected);
  }

  checkCAVCorrectlySelected() {
    const isCAVCorrectlySelected = this.isCAVCorrectlySelected();
    this.isCAVCorrectlySelectedEvent.emit(isCAVCorrectlySelected);
  }

  getCCVSelectedPercentage(): number {
    if (this.ccvFormGroup.value.ccvPercentage === "") {
      return 0;
    }
    return Number(this.ccvFormGroup.value.ccvPercentage);
  }

  getCAVSelectedPercentage(): number {
    if (this.cavFormGroup.value.cavPercentage === "") {
      return 0;
    }
    return Number(this.cavFormGroup.value.cavPercentage);
  }

  private setAccountAmounts(accounts: any): void {
    const ccvAccount = accounts.find((account: any) => account.id === "CCV");
    const cavAccount = accounts.find((account: any) => account.id === "CAV");
    this.ccvAmount = ccvAccount ? ccvAccount.amount : 0;
    this.cavAmount = cavAccount ? cavAccount.amount : 0;
  }

  public ccvCheckboxClick(e: any) {
    if (!this.ccvCheckbox) {
      this.ccvCheckbox = true;
      this.ccvOutlineInput.onFocus();
    } else if (this.ccvCheckbox) {
      this.ccvCheckbox = false;
      this.ccvOutlineInput.cleanInput();
    }
    this.ccvCheckboxChange.emit(this.ccvCheckbox);
    this.ccvSelectedAmountChange();
    this.checkCCVCorrectlySelected();
  }

  ccvSelectedAmountChange() {
    if (this.ccvCheckbox) {
      this.ccvSelectedAmount = (this.ccvAmount * this.getCCVSelectedPercentage()) / 100;
      this.checkCCVCorrectlySelected();
    } else {
      this.ccvSelectedAmount = 0;
    }
    this.ccvSelectedAmount = Math.round(this.ccvSelectedAmount)
    this.ccvSelectedAmountChangeEvent.emit(this.ccvSelectedAmount);
    this.ccvOutPercentage.emit(this.getCCVSelectedPercentage());    
    this.updateSelectedAmount();
  }

  public cavCheckboxClick(e: any) {
    if (!this.cavCheckbox) {
      this.cavCheckbox = true;
      this.cavOutlineInput.onFocus();
    } else if (this.cavCheckbox) {
      this.cavCheckbox = false;
      this.cavOutlineInput.cleanInput();
    }
    this.cavCheckboxChange.emit(this.cavCheckbox);
    this.cavSelectedAmountChange();
    this.checkCAVCorrectlySelected();
  }

  cavSelectedAmountChange() {
    if (this.cavCheckbox) {
      this.cavSelectedAmount = (this.cavAmount * this.getCAVSelectedPercentage()) / 100;
      this.checkCAVCorrectlySelected();
    } else {
      this.cavSelectedAmount = 0;
    }
    this.cavSelectedAmount = Math.round(this.cavSelectedAmount)
    this.cavSelectedAmountChangeEvent.emit(this.cavSelectedAmount);
    this.cavOutPercentage.emit(this.getCAVSelectedPercentage());    
    this.updateSelectedAmount();
  }

  updateSelectedAmount() {
    this.selectedAmount = this.ccvSelectedAmount + this.cavSelectedAmount;
  }

  public getSelectedCCVAmmount(): string {
    if (this.ccvCheckbox) {
      return this.numberToChileanCurrency(this.ccvSelectedAmount);
    } else {
      return this.numberToChileanCurrency(0);
    }
  }

  public getSelectedCAVAmmount(): string {
    if (this.cavCheckbox) {
      return this.numberToChileanCurrency(this.cavSelectedAmount);
    } else {
      return this.numberToChileanCurrency(0);
    }
  }

  public numberToChileanCurrency(value: number): string {
    return this.utils.formatCurrency(value);
  }

  isCCVCorrectlySelected(): boolean {
    return this.ccvCheckbox && this.IsValidPercentage(this.getCCVSelectedPercentage());
  }

  isCAVCorrectlySelected(): boolean {
    return this.cavCheckbox && this.IsValidPercentage(this.getCAVSelectedPercentage());
  }

  IsValidPercentage(percentage: number): boolean {
    return percentage > 0 && percentage <= 100;
  }
}
